import React, { useCallback, useEffect, useState } from 'react';
import { Else, If, Then, When } from 'react-if';
import {
  useFeatureFlagWithLog,
  useQueryWithLog,
} from '@lib/hooks/logging-hooks';
import { useAnalytics } from '@lib/segment/useAnalytics';
import { Box } from '@nerdwallet/currency/components/Box';
import { LoadingSpinner } from '@nerdwallet/currency/components/LoadingSpinner';
import classes from '@nerdwallet/base-styles/classes';
import { Link } from '@nerdwallet/currency/components/Link';

import { FEATURE_FLAGS } from '@constants/feature-flags';
import { PlaidStep } from '@components/financial-institutions/plaid-sandbox/plaid-modal-stepper';

import FinancialInstitution from './financial-institution';
import ManageAccountsSection from './manage-accounts-section';
import { FinancialPortal } from '@generated-gql/generated-types';
import PlaidModal from '@components/financial-institutions/plaid-sandbox/plaid-modal';
import { financialPortalsQuery } from '@components/financial-institutions/plaid-sandbox/queries';
import { usePlaidModalVisibility } from '@components/financial-institutions/plaid-sandbox/plaid-modal-visibility-context';
import { getLogger } from '@lib/logging';

const logger = getLogger('FinancialInstitutions');

const PortalProductState = {
  RETRIEVAL_IN_PROGRESS: 'RETRIEVAL_IN_PROGRESS',
  DATA_RETRIEVED: 'DATA_RETRIEVED',
};

export const isRefreshing = (portal: FinancialPortal) => {
  return (
    portal?.isRefreshing ||
    portal?.accountsState === PortalProductState.RETRIEVAL_IN_PROGRESS ||
    portal?.transactionsState === PortalProductState.RETRIEVAL_IN_PROGRESS
  );
};

export const isSyncing = (portal: FinancialPortal) => {
  return (
    portal?.migrationDetails?.hasUserConfirmed ||
    portal?.isActiveSync ||
    portal?.isRefreshing
  );
};

const isActionRequired = (portal: FinancialPortal) => {
  return (
    portal?.authState === 'USER_ACTION_REQUIRED' &&
    !portal?.migrationDetails?.hasUserConfirmed
  );
};

const FinancialInstitutions = () => {
  const { setShowPlaidVisibility } = usePlaidModalVisibility();

  const [financialPortals, setFinancialPortals] = useState([]);
  const [displayedPortals, setDisplayedPortals] = useState([]);
  const isWithinQuestionnaire = false; // TODO: for the moment this section will not be shown in the net-worth questionnaire
  const { segmentPage } = useAnalytics();

  const [linkingState, setLinkingState] = useState({
    isLinking: false,
    numAccountsLinking: 0,
  });

  const [actionRequiredState, setActionRequiredState] = useState({
    portal: null,
    isActionRequired: false,
    numAccountsActionRequired: 0,
  });

  const {
    data,
    loading,
    refetch: refetchPlaidLinkedAccounts,
  } = useQueryWithLog(logger, financialPortalsQuery, {
    fetchPolicy: 'network-only',
  });

  const { data: plaidModalFeatureFlag } = useFeatureFlagWithLog(
    logger,
    FEATURE_FLAGS.PLAID_MODAL_MIGRATION,
  );

  useEffect(() => {
    if (!loading) {
      const linkedAccounts =
        data?.linkedFinancialPortals?.portals?.length > 0
          ? data.linkedFinancialPortals.portals.map(
              (portal) => portal.marketplaceInstitution?.name,
            )
          : [];
      const additionalAttributes = {
        linked_accounts: linkedAccounts,
      };
      const filteredPortals =
        data?.linkedFinancialPortals?.portals?.filter(
          (portal) => !portal.deleteQueuedAt,
        ) ?? [];
      setFinancialPortals(filteredPortals);
      segmentPage('fhs_verify_linked_accounts', {
        additional_attributes: additionalAttributes,
      });
    }
  }, [loading, data, segmentPage]);

  useEffect(() => {
    let actionRequiredCount = 0;
    let actionRequiredPortal = {};
    let linkingCount = 0;

    financialPortals.forEach((portal: FinancialPortal) => {
      if (isActionRequired(portal)) {
        actionRequiredCount++;
        actionRequiredPortal = portal;
      } else if (isSyncing(portal) && isRefreshing(portal)) {
        linkingCount++;
      }
    });

    setActionRequiredState({
      isActionRequired: actionRequiredCount > 0,
      numAccountsActionRequired: actionRequiredCount,
      portal: actionRequiredPortal,
    });

    setLinkingState({
      isLinking: linkingCount > 0,
      numAccountsLinking: linkingCount,
    });

    setDisplayedPortals(financialPortals);
  }, [financialPortals]);

  const onClickLink = () => {
    setShowPlaidVisibility({
      show: true,
      plaidStep: PlaidStep.PLAID_LINK,
      selectedPortal: null,
    });
  };

  const onClickUnlink = (portal: FinancialPortal) => {
    setShowPlaidVisibility({
      show: true,
      plaidStep: PlaidStep.PLAID_UNLINK,
      selectedPortal: portal,
    });
  };

  const handleClickSeeDetails = useCallback(
    (portal) => {
      setShowPlaidVisibility({
        show: true,
        plaidStep: PlaidStep.PLAID_FIX,
        selectedPortal: portal,
      });
    },
    [setShowPlaidVisibility],
  );

  return (
    <ManageAccountsSection
      isActivated={!loading}
      title="Financial institutions"
    >
      <Box>
        <PlaidModal
          refetchPlaidLinkedAccounts={refetchPlaidLinkedAccounts}
          plaidFooterProps={{ linkingState, actionRequiredState }}
        />

        <When condition={loading && displayedPortals.length === 0}>
          <div
            className={classes(
              'margin-vertical-4',
              'display-flex',
              'justify-content-center',
            )}
          >
            <LoadingSpinner />
          </div>
        </When>
        <When condition={!loading || displayedPortals.length > 0}>
          <div className={classes('margin-top-3')}>
            {displayedPortals?.map((portal: FinancialPortal) => {
              return (
                <div key={portal.id}>
                  <If condition={isWithinQuestionnaire}>
                    <Then>
                      <FinancialInstitution
                        key={portal?.id}
                        onClickUnlink={() => onClickUnlink(portal)}
                        portal={portal}
                        onClickSeeDetails={() => handleClickSeeDetails(portal)}
                      />
                    </Then>
                    <Else>
                      <FinancialInstitution
                        key={portal?.id}
                        onClickUnlink={() => onClickUnlink(portal)}
                        portal={portal}
                        onClickSeeDetails={() => handleClickSeeDetails(portal)}
                      />
                    </Else>
                  </If>
                </div>
              );
            })}
          </div>
          <If condition={plaidModalFeatureFlag?.value}>
            <Then>
              <div className={classes('margin-top-3')}>
                <Link onClick={onClickLink}>
                  {displayedPortals?.length > 0
                    ? 'Link another account'
                    : 'Link your first account'}
                </Link>
              </div>
            </Then>
          </If>
        </When>
      </Box>
    </ManageAccountsSection>
  );
};

export default FinancialInstitutions;
